import React, { useEffect, useMemo, useState } from "react";

import FilterMenuButton from "../../common/FilterMenuButton";
import EnvironmentalContextCell from "../../results/EnvironmentalContextCell";
import { useDateTimeFormat } from "../../../hooks/useDateTimeFormat";
import {
  filterQuestionnaireEntries,
  getQuestionnaireTypeFilterLabel,
  getQuestionnaireTypeFilterOptions,
} from "../../../utils/patientProfileFilters";
import { getQuestionnaireScoreParts } from "../../../utils/patientProfileData";

function createDefaultQuestionnaireFilters() {
  return {
    types: [],
  };
}

export default function PatientQuestionnaireTable({
  title,
  count,
  entries = [],
  showScores = true,
  showEnvironmentalContext = false,
  showWeatherDetails = true,
  t,
}) {
  const { formatDateTime } = useDateTimeFormat();
  const [filters, setFilters] = useState(createDefaultQuestionnaireFilters);

  useEffect(() => {
    setFilters(createDefaultQuestionnaireFilters());
  }, [entries]);

  const questionnaireTypeOptions = useMemo(
    () => getQuestionnaireTypeFilterOptions(entries, t),
    [entries, t],
  );

  const filteredEntries = useMemo(
    () => filterQuestionnaireEntries(entries, filters),
    [entries, filters],
  );

  const hasActiveFilters = filters.types.length > 0;
  const emptyMessage =
    entries.length > 0 && hasActiveFilters
      ? t(
          "profilePage.noFilteredEntries",
          "No entries match the selected filters.",
        )
      : t("profilePage.noEntries");

  const toggleFilterValue = (_sectionKey, value) => {
    setFilters((current) => {
      const nextValues = new Set(current.types);

      if (nextValues.has(value)) {
        nextValues.delete(value);
      } else {
        nextValues.add(value);
      }

      return {
        types: [...nextValues],
      };
    });
  };

  return (
    <section className="card flex h-full flex-col overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-greyscale-1 p-2">
        <h4 className="text-base font-semibold">{title}</h4>

        <FilterMenuButton
          buttonClassName="small-outlined-button !min-h-0 !px-3 !py-1.5"
          buttonLabel={t("profilePage.questionnairesFilterButton", "Filter")}
          clearLabel={t("profilePage.clearFilters", "Clear")}
          emptyLabel={t("profilePage.noFilterOptions", "No filters available.")}
          sections={[
            {
              key: "types",
              title: t("profilePage.questionnaireTypeHeader", "Type"),
              options: questionnaireTypeOptions,
            },
          ]}
          selectedValuesBySection={filters}
          onToggleValue={toggleFilterValue}
          onClearAll={() => setFilters(createDefaultQuestionnaireFilters())}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto bg-container">
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            <col className="w-16" />
            <col className="w-[30%]" />
            <col className={showScores ? "w-[22%]" : "w-[27%]"} />
            <col className={showScores ? "w-[22%]" : "w-[27%]"} />
            {showEnvironmentalContext ? <col className="w-[28%]" /> : null}
            {showScores ? <col className="w-[18%]" /> : null}
          </colgroup>

          <thead className="sticky top-0 z-10 bg-container shadow-[0_1px_0_0_var(--color-greyscale-1)]">
            <tr className="border-b border-greyscale-1 text-sm text-typography">
              <th scope="col" className="px-3 py-3 text-left font-medium">
                {t("profilePage.hashHeader")}
              </th>

              <th scope="col" className="px-3 py-3 text-left font-medium">
                {t("profilePage.questionnaireTypeHeader", "Type")}
              </th>

              <th scope="col" className="px-3 py-3 text-center font-medium">
                {t("profilePage.dateHeader")}
              </th>

              <th scope="col" className="px-3 py-3 text-center font-medium">
                {t("profilePage.timeHeader")}
              </th>

              {showEnvironmentalContext ? (
                <th scope="col" className="px-3 py-3 text-left font-medium">
                  {t("profilePage.environmentHeader", "Environment")}
                </th>
              ) : null}

              {showScores ? (
                <th scope="col" className="px-3 py-3 text-center font-medium">
                  {t("profilePage.scoreHeader")}
                </th>
              ) : null}
            </tr>
          </thead>

          <tbody>
            {filteredEntries.length ? (
              filteredEntries.map((item, index, items) => {
                const dateTime = formatDateTime(item?.takenAt);
                const questionnaireTypeLabel = getQuestionnaireTypeFilterLabel(
                  item,
                  t,
                );
                const scoreParts = getQuestionnaireScoreParts(
                  item.score,
                  item.questionnaireType,
                );

                return (
                  <tr
                    key={`${item.uid}-${item.docId}-${item.questionnaireType}`}
                    className="border-b border-greyscale-1 transition-colors hover:bg-greyscale-0.5"
                  >
                    <td className="patient-list-container">
                      {items.length - index}
                    </td>

                    <td
                      className="patient-list-container truncate"
                      title={questionnaireTypeLabel}
                    >
                      {questionnaireTypeLabel}
                    </td>

                    <td className="patient-list-container text-center">
                      {dateTime.date || "--"}
                    </td>

                    <td className="patient-list-container text-center">
                      {dateTime.time || "--"}
                    </td>

                    {showEnvironmentalContext ? (
                      <td className="patient-list-container align-top">
                        <EnvironmentalContextCell
                          context={item.environmentalContext}
                          t={t}
                          showWeatherDetails={showWeatherDetails}
                        />
                      </td>
                    ) : null}

                    {showScores ? (
                      <td className="patient-list-container text-center font-medium text-primary">
                        {scoreParts ? (
                          <>
                            {scoreParts.score}
                            {scoreParts.maxScore !== null ? (
                              <span className="font-normal text-greyscale-3">
                                {" / "}
                                {scoreParts.maxScore}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={
                    (showScores ? 5 : 4) + (showEnvironmentalContext ? 1 : 0)
                  }
                  className="patient-list-container py-10 text-center text-greyscale-3"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
